/**
 * Owner Dashboard redesign (AB#2206) — the data behind the "today" half of the
 * Dashboard, all from existing endpoints:
 *   • branches            GET /salon-owner/branches
 *   • today's bookings    GET /salon-owner/appointments/list-v2 (from = to = today)
 *   • awaiting you        list-v2 status=Pending (same filter as the Appointments "Pending" tab)
 *   • completed, unpaid   list-v2 status=Completed & paymentStatus=Unpaid
 *   • on shift            GET /salon-owner/calendar/grid per branch (the Calendar's own data)
 * "Today" and "now" are the owner's browser clock — the same rule as the O1
 * dashboard window (pilot salons are single-timezone, UAE / Jordan, and owners
 * work in that zone). Query keys are the shared ones, so booking actions anywhere (which invalidate
 * the appointments + calendar caches) refresh the Dashboard too.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { queryKeys } from '../../../lib/queryKeys';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useSalonOwnerAppointments } from '../../../hooks/useSalonOwnerAppointments';
import type { SalonOwnerAppointmentRowDto } from '../../../services/appointmentsList';
import type { BranchDto, CalendarGridResponse } from '../../../types/api-responses';
import { localHHmm, localYmd, sortByStart } from './dashboardModel';

const ONE_MINUTE = 60_000;

/** Re-render every minute so "now", "next" and "updated … ago" stay true. */
export function useMinuteClock(): Date {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = window.setInterval(() => setNow(new Date()), 60_000);
        return () => window.clearInterval(id);
    }, []);
    return now;
}

export function useActiveBranches() {
    const { isSalonOwner } = useAuthContext();
    const q = useQuery({
        queryKey: queryKeys.branches.list(),
        queryFn: () => apiService.getBranches(),
        enabled: isSalonOwner,
        staleTime: 60_000,
    });
    const branches = useMemo<BranchDto[]>(
        () => (q.data?.branches ?? []).filter(b => b.active),
        [q.data],
    );
    return { branches, isLoading: q.isLoading };
}

export function useOwnerToday(branchIds: number[], branches: BranchDto[], now: Date) {
    const today = localYmd(now);
    const branchCsv = branchIds.length ? branchIds.join(',') : undefined;

    // No Refresh button: the three Dashboard lists poll every minute (plus refetch on focus / after actions).
    const poll = { refetchInterval: ONE_MINUTE };
    const todayQ = useSalonOwnerAppointments({ from: today, to: today, branchIds: branchCsv, pageSize: 100 }, poll);
    const pendingQ = useSalonOwnerAppointments({ status: 'Pending', branchIds: branchCsv, pageSize: 1 }, poll);
    const unpaidQ = useSalonOwnerAppointments({ status: 'Completed', paymentStatus: 'Unpaid', branchIds: branchCsv, pageSize: 100 }, poll);

    const scope = branchIds.length ? branches.filter(b => branchIds.includes(b.id)) : branches;
    const gridQs = useQueries({
        queries: scope.map(b => ({
            queryKey: queryKeys.calendar.grid({ branchId: b.id, date: today }),
            queryFn: () => apiService.getCalendarGrid({ branchId: b.id, date: today }),
            staleTime: 30_000,
            refetchInterval: ONE_MINUTE,
        })),
    });

    const rows = useMemo<SalonOwnerAppointmentRowDto[]>(
        () => sortByStart(todayQ.data?.itemsForOwner ?? []),
        [todayQ.data],
    );

    const grids = scope
        .map((b, i) => ({ branch: b, grid: gridQs[i]?.data as CalendarGridResponse | undefined }))
        .filter((g): g is { branch: BranchDto; grid: CalendarGridResponse } => g.grid != null);

    const count = (c?: { paid: number; unpaid: number; refunded: number }) =>
        c ? c.paid + c.unpaid + c.refunded : 0;

    const unpaidRows = unpaidQ.data?.itemsForOwner ?? [];

    return {
        today,
        nowHHmm: localHHmm(now),
        rows,
        todayHasMore: todayQ.data?.hasMore ?? false,
        todayLoading: todayQ.isLoading,
        todayError: todayQ.isError,
        todayUpdatedAt: todayQ.dataUpdatedAt,
        refetchToday: todayQ.refetch,
        pending: {
            count: count(pendingQ.data?.counts),
            earliest: pendingQ.data?.itemsForOwner?.[0] ?? null,
            loading: pendingQ.isLoading,
            error: pendingQ.isError,
        },
        unpaid: {
            count: unpaidQ.data?.counts?.unpaid ?? 0,
            amount: unpaidRows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
            amountIsPartial: unpaidQ.data?.hasMore ?? false,
            currency: unpaidRows[0]?.currencyIso,
            loading: unpaidQ.isLoading,
            error: unpaidQ.isError,
        },
        grids,
        rosterLoading: gridQs.some(q => q.isLoading),
        rosterError: gridQs.some(q => q.isError),
    };
}
